import { createClient, type V5Client } from "@mfa/v5-client";
import { PROCEDURE_ACCESS, readApiError } from "@mfa/v5-contracts";
import { type ReleaseWatch, releaseOf } from "./release-watch.js";

/**
 * The site's packaged client (`@mfa/v5-client`, V6 plan H.17 and H.18): every call the site makes
 * (ApiClient's methods, the builder console's run client, the /dev tools) goes out through it. It
 * speaks the plain JSON wire with the session cookie, through one fetch that keeps the site's
 * rules for every answer: each answer's release goes to the release watch; an answer this build
 * cannot decode from a newer release reloads the page (release-watch.ts), an unreadable error body
 * and, since H1b.17, a successful procedure answer that is not JSON (a page, text); and a 401
 * outside the sign-in procedures means the session cookie is dead (`onUnauthorized`), the routes'
 * and the stream's included.
 */
export interface WebV5ClientOptions {
  /**
   * ApiClient's base, `/api/v5` by default. The URLs the page renders (`routes`: a deliverable's
   * Download link) keep it as given, page-relative; the fetch path resolves a relative base against
   * the page's origin when the client is built, or against `http://localhost` where the page has
   * none (a stubbed location in a test, a prerender), as the site's own link always did.
   */
  baseUrl: string;
  releases: ReleaseWatch;
  onUnauthorized(): void;
  /** The transport; globalThis.fetch, read at each call, by default. */
  fetch?: typeof fetch | undefined;
}

/**
 * The sign-in procedures answer 401 while a person signs in: not a dead session. A 401 from any
 * other call, `identity.updatePreferences` and the hand-written routes included, is one.
 */
const IDENTITY_CALL =
  /\/identity\/(?:bootstrap|exchange|logout|revokeOtherSessions|validateContinuation)$/;

/** A procedure's call (POST /api/v5/<area>/<name>), whose answer the typed client decodes. */
function isProcedureCall(url: string): boolean {
  const match = /\/([^/?#]+)\/([^/?#]+)(?:[?#]|$)/.exec(url);
  return match !== null && PROCEDURE_ACCESS.has(`${match[1]}.${match[2]}`);
}

/**
 * Whether a successful procedure answer decodes, read from a copy (the client reads the body).
 * The body is read the way oRPC's fetch link reads it (toStandardBody in
 * @orpc/standard-server-fetch 1.15.5), by its headers alone:
 * - a `content-disposition` makes it a file, never the output;
 * - no content type, or `application/json`, is JSON, which must parse (an empty body is no body,
 *   the answer of a procedure without output);
 * - `multipart/form-data` is the form data a blob-bearing answer is;
 * - anything else (`text/plain`, a page, a form, a stream, a file by type) is a value that is not
 *   the output, even when its text would parse as one.
 */
async function decodes(response: Response): Promise<boolean> {
  if (response.headers.get("content-disposition") !== null) return false;
  const type = response.headers.get("content-type");
  if (type?.startsWith("multipart/form-data")) return true;
  if (type && !type.startsWith("application/json")) return false;
  const text = await response
    .clone()
    .text()
    .catch(() => null);
  if (text === null) return false;
  if (!text) return true;
  try {
    JSON.parse(text);
    return true;
  } catch {
    return false;
  }
}

function urlOf(input: string | URL | Request): string {
  return typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
}

/** An absolute base for the packaged client, which refuses a relative one it cannot resolve. */
function absoluteBase(baseUrl: string): string {
  if (/^[a-z][a-z0-9+.-]*:/i.test(baseUrl)) return baseUrl;
  const origin = (globalThis as { location?: { origin?: string } }).location?.origin;
  return new URL(baseUrl, origin && origin !== "null" ? origin : "http://localhost").href;
}

export function createWebV5Client(options: WebV5ClientOptions): V5Client {
  /** Releases whose successful answer did not decode: reported to the watch once each. */
  const undecoded = new Set<string>();
  const send: typeof fetch = async (input, init) => {
    const response = await (options.fetch ?? globalThis.fetch)(input, init);
    const release = releaseOf(response.headers);
    options.releases.observe(release);
    if (response.status >= 400) {
      if (response.status === 401 && !IDENTITY_CALL.test(urlOf(input))) options.onUnauthorized();
      // Read from a copy: the packaged client reads the refusal itself.
      const body = await response
        .clone()
        .json()
        .catch(() => null);
      if (!readApiError(response.status, body, response.headers.get("x-request-id")))
        options.releases.undecodable(release);
    } else if (
      // Only another release's answer can be from a newer build (the watch ignores the page's own),
      // so the page's own answers are never read twice.
      response.ok &&
      release !== null &&
      release !== options.releases.getSnapshot().loaded &&
      !undecoded.has(release) &&
      isProcedureCall(urlOf(input)) &&
      !(await decodes(response))
    ) {
      undecoded.add(release);
      options.releases.undecodable(release);
    }
    return response;
  };
  const fetching = createClient({
    baseUrl: absoluteBase(options.baseUrl),
    credential: { kind: "cookie" },
    fetch: send,
  });
  // The route URLs the page renders keep the base as given (page-relative for `/api/v5`), as the
  // site's own links always were; only the calls resolve it. The packaged client's own table
  // builds them, over the base it was handed.
  const { routes } = createClient({ baseUrl: options.baseUrl, credential: { kind: "cookie" } });
  return { ...fetching, routes };
}
